import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  CheckCircle,
  MapPin,
  Home,
  Briefcase,
  TrendingUp,
  Users,
  Calendar,
  DollarSign,
  Star,
  Sparkles,
  ArrowRight,
  Shield,
  Clock,
  LayoutDashboard,
  MessageCircle,
  Target,
  Zap,
  Award,
  BarChart3,
  CreditCard,
  Heart,
  Globe,
  Phone,
  PlayCircle,
  Rocket,
  TrendingDown,
  Infinity,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const BRAND_DARK = "#252F4F";
const BRAND_GREEN = "#98C454";
const BRAND_GRAY = "#81869a";

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeFeature, setActiveFeature] = useState(0);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-purple-50 text-gray-900 overflow-x-hidden">
      {/* Modern Floating Header */}
      <header className="fixed top-0 left-0 right-0 z-50 pt-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/90 backdrop-blur-2xl border border-gray-200 rounded-full px-6 py-3 shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#98C454] to-[#22c55e] flex items-center justify-center font-bold text-white">
                  B
                </div>
                <div>
                  <h1 className="text-sm font-bold text-[#252F4F]">
                    BookaTherapy
                  </h1>
                  <p className="text-[10px] text-gray-600">For Practitioners</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {user ? (
                  <Button
                    onClick={() => navigate("/dashboard")}
                    className="bg-[#98C454] hover:bg-[#87b043] text-white rounded-full px-6 font-semibold">
                    Dashboard
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      onClick={() => navigate("/login")}
                      className="text-[#252F4F] hover:bg-gray-100 rounded-full font-medium">
                      Sign In
                    </Button>
                    <Button
                      onClick={() =>
                        (window.location.href =
                          "https://clinic.bookatherapy.com/clinic-signups")
                      }
                      className="bg-gradient-to-r from-[#98C454] to-[#22c55e] hover:shadow-lg hover:shadow-[#98C454]/50 text-white rounded-full px-6 font-bold transition-all">
                      Get Started Free
                      <Sparkles className="ml-2 h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Revolutionary Hero Section - Split Design */}
      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000008_1px,transparent_1px),linear-gradient(to_bottom,#00000008_1px,transparent_1px)] bg-[size:4rem_4rem]" />

        {/* Gradient Orbs */}
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#98C454] rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob" />
        <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-purple-400 rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob animation-delay-2000" />
        <div className="absolute bottom-0 left-1/2 w-[500px] h-[500px] bg-pink-400 rounded-full mix-blend-multiply filter blur-[128px] opacity-30 animate-blob animation-delay-4000" />

        <div className="container mx-auto px-6 lg:px-12 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Epic Typography */}
            <div className="space-y-8">
              <div className="inline-flex items-center space-x-2 bg-white/80 border border-[#98C454]/30 rounded-full px-4 py-2 backdrop-blur-sm shadow-lg">
                <div className="h-2 w-2 bg-[#98C454] rounded-full animate-pulse" />
                <span className="text-sm font-semibold text-[#252F4F]">
                  Trusted by 500+ Therapists
                </span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.9] tracking-tighter">
                <span className="block text-[#252F4F]">Your Practice.</span>
                <span className="block bg-gradient-to-r from-[#98C454] via-[#22c55e] to-[#10b981] bg-clip-text text-transparent animate-gradient bg-[length:200%_auto]">
                  Your Rules.
                </span>
                <span className="block text-gray-400 text-4xl sm:text-5xl lg:text-6xl mt-4">
                  Zero Cost.
                </span>
              </h1>

              <p className="text-xl lg:text-2xl text-gray-700 leading-relaxed max-w-xl">
                The revolutionary platform where massage therapists build their
                brand, connect with clients, and grow revenue—completely free.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <Button
                  size="lg"
                  onClick={() =>
                    user
                      ? navigate("/dashboard")
                      : (window.location.href =
                          "https://clinic.bookatherapy.com/clinic-signups")
                  }
                  className="group bg-gradient-to-r from-[#98C454] to-[#22c55e] hover:shadow-2xl hover:shadow-[#98C454]/50 text-white rounded-2xl px-10 py-8 text-lg font-bold transition-all hover:scale-105">
                  {user ? "Go to Dashboard" : "Start Building Free"}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#252F4F]/20 hover:bg-[#252F4F]/5 text-[#252F4F] rounded-2xl px-10 py-8 text-lg font-semibold backdrop-blur-sm">
                  Watch Demo
                  <PlayCircle className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-8">
                <div className="flex -space-x-3">
                  {["💆‍♀️", "💆‍♂️", "💆", "🧖‍♀️"].map((emoji, i) => (
                    <div
                      key={i}
                      className="h-12 w-12 rounded-full bg-white backdrop-blur-sm border-2 border-gray-200 shadow-lg flex items-center justify-center text-xl">
                      {emoji}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 fill-[#98C454] text-[#98C454]"
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">
                    Loved by therapists nationwide
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Futuristic Bento Grid */}
            <div className="grid grid-cols-2 gap-4 auto-rows-[200px]">
              {/* Large Feature Card */}
              <Card className="col-span-2 row-span-2 bg-gradient-to-br from-[#98C454] to-[#22c55e] border-none rounded-3xl overflow-hidden group hover:scale-105 transition-transform shadow-2xl">
                <CardContent className="p-8 h-full flex flex-col justify-between relative">
                  <div className="absolute inset-0 bg-white/10" />
                  <div className="relative z-10">
                    <DollarSign className="h-12 w-12 text-white mb-4" />
                    <h3 className="text-3xl font-black text-white mb-2">
                      100% Free
                    </h3>
                    <p className="text-white/90">
                      Forever. No hidden fees. No catch.
                    </p>
                  </div>
                  <div className="relative z-10 text-6xl font-black text-white/30">
                    $0
                  </div>
                </CardContent>
              </Card>

              {/* Small Feature Cards */}
              <Card className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl overflow-hidden hover:shadow-xl transition-all group">
                <CardContent className="p-6 h-full flex flex-col justify-center items-center text-center">
                  <Users className="h-8 w-8 text-[#98C454] mb-3 group-hover:scale-110 transition-transform" />
                  <p className="text-2xl font-bold text-[#252F4F]">500+</p>
                  <p className="text-xs text-gray-600">Active Therapists</p>
                </CardContent>
              </Card>

              <Card className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-3xl overflow-hidden hover:shadow-xl transition-all group">
                <CardContent className="p-6 h-full flex flex-col justify-center items-center text-center">
                  <Star className="h-8 w-8 text-yellow-400 mb-3 group-hover:scale-110 transition-transform" />
                  <p className="text-2xl font-bold text-[#252F4F]">4.9</p>
                  <p className="text-xs text-gray-600">Avg Rating</p>
                </CardContent>
              </Card>

              <Card className="col-span-2 bg-gradient-to-br from-purple-100 to-pink-100 backdrop-blur-xl border border-purple-200 rounded-3xl overflow-hidden hover:scale-105 transition-transform shadow-lg">
                <CardContent className="p-6 h-full flex items-center justify-between">
                  <div>
                    <h4 className="text-xl font-bold text-[#252F4F] mb-1">
                      Multi-Location
                    </h4>
                    <p className="text-sm text-gray-700">
                      Clinic, Home & Mobile
                    </p>
                  </div>
                  <MapPin className="h-10 w-10 text-purple-400" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Rotating Features Showcase */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-black text-[#252F4F] mb-6">
              Everything You Need.{" "}
              <span className="bg-gradient-to-r from-[#98C454] to-[#22c55e] bg-clip-text text-transparent">
                Nothing You Don't.
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Purpose-built tools designed to help you succeed without the
              complexity
            </p>
          </div>

          {/* Interactive Feature Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Rocket,
                title: "Launch in Minutes",
                desc: "Complete setup in under 5 minutes",
                color: "from-orange-500 to-red-500",
              },
              {
                icon: Infinity,
                title: "Unlimited Growth",
                desc: "No caps on bookings or revenue",
                color: "from-blue-500 to-cyan-500",
              },
              {
                icon: Shield,
                title: "Bank-Level Security",
                desc: "Your data is always protected",
                color: "from-green-500 to-emerald-500",
              },
              {
                icon: Zap,
                title: "Instant Bookings",
                desc: "Real-time availability updates",
                color: "from-purple-500 to-pink-500",
              },
            ].map((feature, index) => (
              <Card
                key={index}
                onMouseEnter={() => setActiveFeature(index)}
                className={`group relative overflow-hidden border-2 border-gray-200 rounded-3xl transition-all duration-500 bg-white ${
                  activeFeature === index
                    ? "scale-105 shadow-2xl"
                    : "scale-100 hover:scale-105"
                }`}>
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-5 group-hover:opacity-10 transition-opacity`}
                />
                <CardContent className="relative p-8 space-y-4">
                  <div
                    className={`h-16 w-16 rounded-2xl bg-gradient-to-br ${feature.color} p-4 shadow-lg`}>
                    <feature.icon className="h-full w-full text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#252F4F]">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section - Modern Accordion */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-black text-[#252F4F] mb-6">
              Questions?{" "}
              <span className="bg-gradient-to-r from-[#98C454] to-[#22c55e] bg-clip-text text-transparent">
                Answered.
              </span>
            </h2>
            <p className="text-xl text-gray-600">Everything you need to know</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                q: "Is it really 100% free forever?",
                a: "Yes! Zero setup fees, zero monthly costs, zero commissions. We only succeed when you do. No credit card required to start.",
              },
              {
                q: "How quickly can I start getting bookings?",
                a: "Most therapists receive their first booking within 48 hours of completing their profile. The average is 3-5 bookings in the first week.",
              },
              {
                q: "Can I manage multiple practice locations?",
                a: "Absolutely! Manage clinic, home-based, and mobile services all from one dashboard. Set different rates and availability for each.",
              },
              {
                q: "How do payments work?",
                a: "Secure, instant payments directly to your account. We handle all payment processing with bank-level security.",
              },
              {
                q: "What makes BookaTherapy different?",
                a: "We're the only platform built exclusively for massage therapists. No commissions, unlimited bookings, and tools designed specifically for your practice.",
              },
            ].map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white backdrop-blur-xl border-2 border-gray-200 rounded-2xl px-8 hover:shadow-lg transition-all">
                <AccordionTrigger className="text-left font-bold text-lg text-[#252F4F] hover:no-underline py-6">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-6 leading-relaxed text-base">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA - Bold and Direct */}
      <section className="py-32 bg-gradient-to-br from-[#98C454] via-[#22c55e] to-[#10b981] relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff15_1px,transparent_1px),linear-gradient(to_bottom,#ffffff15_1px,transparent_1px)] bg-[size:4rem_4rem]" />

        <div className="container mx-auto px-6 lg:px-12 max-w-5xl relative z-10 text-center">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-6 py-3 mb-8 shadow-lg">
            <Target className="h-5 w-5 text-white" />
            <span className="text-sm font-semibold text-white">
              Limited Time: Early Adopter Benefits
            </span>
          </div>

          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-8 leading-tight drop-shadow-lg">
            Ready to Transform
            <br />
            Your Practice?
          </h2>

          <p className="text-2xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            Join hundreds of successful therapists building their empires on
            BookaTherapy. No credit card. No risk. Just growth.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Button
              size="lg"
              onClick={() =>
                user
                  ? navigate("/dashboard")
                  : (window.location.href =
                      "https://clinic.bookatherapy.com/clinic-signups")
              }
              className="group bg-white text-[#252F4F] hover:bg-white/90 rounded-2xl px-12 py-8 text-xl font-black shadow-2xl hover:scale-105 transition-all">
              {user ? "Go to Dashboard" : "Start Building Free"}
              <Rocket className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate("/login")}
              className="border-2 border-white/40 hover:bg-white/10 text-white rounded-2xl px-12 py-8 text-xl font-bold backdrop-blur-sm">
              Sign In
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Shield, text: "100% Secure" },
              { icon: DollarSign, text: "Always Free" },
              { icon: Clock, text: "5-Min Setup" },
              { icon: Globe, text: "Canada-Wide" },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/30 mb-4 shadow-lg">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <p className="text-white font-semibold">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-[#98C454] to-[#22c55e] flex items-center justify-center font-bold text-white">
                B
              </div>
              <div>
                <p className="font-bold text-[#252F4F]">BookaTherapy</p>
                <p className="text-sm text-gray-600">
                  &copy; 2025 All rights reserved
                </p>
              </div>
            </div>
            <div className="flex gap-8">
              {["Privacy", "Terms", "Contact"].map((link) => (
                <a
                  key={link}
                  href={`https://bookatherapy.com/${link
                    .toLowerCase()
                    .replace(" ", "-")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-gray-600 hover:text-[#252F4F] transition-colors">
                  {link}
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes gradient {
          0%,
          100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
        @keyframes blob {
          0%,
          100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
